import { useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import type {
  AssetsSearchGroupBy,
  AssetsSearchOrderBy,
} from '@terra/shared-contracts-assets';
import { useUnifiedAssetsData } from './useUnifiedAssetsData';
import { useTabScopedAssetsFilters } from '../store/tab/assetsTabScopedFilterStore';
import { useArchivedLifecycleFilter } from './useArchivedLifecycleFilter';
import type { AssetsSearchRequestFilters } from './api/assets-search-request';

/**
 * Custom hook that encapsulates filter subscriptions and data fetching
 * This prevents the parent component from re-rendering when filters change
 */
export function useAssetsWithFilters({
  selectedNodes,
  orderBy,
  groupBy,
}: {
  selectedNodes: string[];
  orderBy?: AssetsSearchOrderBy[];
  groupBy?: AssetsSearchGroupBy;
}) {
  const {
    searchTerm,
    setSearchTerm,
    typeFilter,
    setTypeFilter,
    securityFilter,
    detectionView,
    verified,
    setVerified,
    unverifiedStatuses,
    setUnverifiedStatuses,
    methodsFilter,
    setMethodsFilter,
    apiTypes,
    withCodeGraph,
    setWithCodeGraph,
    createdStartDate,
    setCreatedStartDate,
    createdEndDate,
    setCreatedEndDate,
    createdSince,
    setCreatedSince,
    updatedStartDate,
    setUpdatedStartDate,
    updatedEndDate,
    setUpdatedEndDate,
    lifecycleState,
    setLifecycleState,
    resetFilters,
  } = useTabScopedAssetsFilters();
  const { isLifecycleUiEnabled, lifecycleQueryParam } =
    useArchivedLifecycleFilter({ lifecycleState });

  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 500);

  const assetsData = useUnifiedAssetsData({
    typeFilter,
    methodsFilter,
    apiTypes,
    securityFilter,
    detectionView,
    orderBy,
    groupBy,
    withCodeGraph,
    searchTerm: debouncedSearchTerm,
    createdStartDate,
    createdEndDate,
    createdSince,
    updatedStartDate,
    updatedEndDate,
    selectedNodes,
    verified,
    unverifiedStatuses,
    lifecycleState: lifecycleQueryParam,
  });

  const requestFilters = useMemo(
    (): AssetsSearchRequestFilters => ({
      typeFilter,
      methodsFilter,
      apiTypes,
      searchTerm: debouncedSearchTerm,
      securityFilter,
      detectionView,
      createdStartDate,
      createdEndDate,
      createdSince,
      updatedStartDate,
      updatedEndDate,
      withCodeGraph,
      orderBy,
      domainFilters: assetsData.domainFilters,
      lifecycleState: lifecycleQueryParam,
    }),
    [
      apiTypes,
      assetsData.domainFilters,
      createdEndDate,
      createdSince,
      createdStartDate,
      debouncedSearchTerm,
      detectionView,
      lifecycleQueryParam,
      methodsFilter,
      orderBy,
      securityFilter,
      typeFilter,
      updatedEndDate,
      updatedStartDate,
      withCodeGraph,
    ],
  );

  const isInitialLoading = useMemo(
    () => assetsData.isLoading && !assetsData.data,
    [assetsData.isLoading, assetsData.data],
  );

  const isRefetching = useMemo(
    () => assetsData.isFetching && !!assetsData.data,
    [assetsData.isFetching, assetsData.data],
  );

  return {
    assets: assetsData.assets,
    endpoints: assetsData.endpoints,
    webPages: assetsData.webPages,
    totalCount: assetsData.totalCount,
    groups: assetsData.groups,
    isInitialLoading,
    isRefetching,
    hasNextPage: assetsData.hasNextPage,
    isFetchingNextPage: assetsData.isFetchingNextPage,
    fetchNextPage: assetsData.fetchNextPage,
    refetch: assetsData.refetch,
    requestFilters,

    searchTerm,
    setSearchTerm,
    typeFilter,
    setTypeFilter,
    securityFilter,
    detectionView,
    apiTypes,
    verified,
    setVerified,
    unverifiedStatuses,
    setUnverifiedStatuses,
    methodsFilter,
    setMethodsFilter,
    withCodeGraph,
    setWithCodeGraph,
    createdStartDate,
    setCreatedStartDate,
    createdEndDate,
    setCreatedEndDate,
    createdSince,
    setCreatedSince,
    updatedStartDate,
    setUpdatedStartDate,
    updatedEndDate,
    setUpdatedEndDate,
    lifecycleState,
    setLifecycleState,
    isLifecycleUiEnabled,
    lifecycleQueryParam,
    resetFilters,
  };
}
